import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { formatPrice } from "@/utils/currency";
import { t } from "@/utils/locale";

export default function DashboardDailyStats({ stats, analytics, reviewStats, canSeeFinancials }) {

  const rs = reviewStats?.count > 0
    ? reviewStats
    : (stats?.avg_rating != null && stats?.reviews_count > 0)
      ? { avg: stats.avg_rating, count: stats.reviews_count, ...(stats.reviews_distribution || {}) }
      : null;

  const dailyStatCards = [
    { label: t("ordersCount"), value: stats?.today_orders || 0, id: "orders" },
    { label: t("cancelledOrders"), value: stats?.cancelled_today || 0, id: "cancelled" },
    ...(canSeeFinancials ? [
      { label: t("revenue"), value: formatPrice(stats?.today_revenue), id: "revenue" },
      { label: t("avgTicket"), value: formatPrice(stats?.ticket_medio), id: "ticket" },
    ] : []),
    { label: t("reviews"), value: stats?.reviews_count || 0, id: "reviews" },
    { label: t("comments"), value: stats?.comments_count || 0, id: "comments" },
  ];

  return (
    <>
      <div>
        <h2 className="text-lg font-bold uppercase tracking-tight mb-4" data-testid="daily-tracking-title">{t("dailyTracking")}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3" data-testid="daily-stats">
          {dailyStatCards.map(s => (
            <Card key={s.id} className="bg-card border-border/50 text-center">
              <CardContent className="py-4">
                <p className="text-sm text-muted-foreground">{s.label}</p>
                <p className="text-3xl font-bold mt-1" data-testid={`stat-${s.id}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>


      {rs && (
        <Card className="bg-card border-border/50" data-testid="reviews-stats-card">
          <CardContent className="py-4">
            <h3 className="font-bold uppercase text-sm mb-3">Avaliações</h3>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="text-center min-w-[80px]">
                <p className="text-4xl font-bold text-amber-500">{(rs.avg || 0).toFixed(1)}</p>
                <div className="flex justify-center gap-0.5 mt-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className={`text-base ${star <= Math.round(rs.avg || 0) ? "text-amber-400" : "text-gray-200"}`}>★</span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{rs.count} avaliações</p>
              </div>
              <div className="flex-1 min-w-[180px] space-y-1.5">
                {[5, 4, 3, 2, 1].map(star => {
                  const cnt = rs[`r${star}`] || 0;
                  const pct = rs.count > 0 ? (cnt / rs.count) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-3 text-right">{star}</span>
                      <span className="text-amber-400 text-xs">★</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-5 text-right">{cnt}</span>
                    </div>
                  );
                })}
              </div>
              {rs.recent_30d !== undefined && rs.prior_30d !== undefined && (() => {
                const trend = rs.prior_30d > 0
                  ? Math.round(((rs.recent_30d - rs.prior_30d) / rs.prior_30d) * 100)
                  : rs.recent_30d > 0 ? 100 : null;
                if (trend === null) return null;
                return (
                  <div className="text-center min-w-[60px]">
                    <p className={`text-xl font-bold ${trend >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {trend >= 0 ? "+" : ""}{trend}%
                    </p>
                    <p className="text-xs text-muted-foreground">vs 30d ant.</p>
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
